class PdpBundleSelector extends HTMLElement {
  disconnectedCallback() {
    this.stickyCartObserver?.disconnect();
    document.removeEventListener('cart:update', this.handleStickyCartUpdate);
    window.removeEventListener('cart:error', this.handleStickyCartError);
    clearTimeout(this.stickyCartFeedbackTimeout);
    clearTimeout(this.stickyCartSlowRequestTimeout);
    this.unlockOfferSelection();
    this.stickyCart?.remove();
  }

  connectedCallback() {
    this.radios = Array.from(this.querySelectorAll('[data-variant-id]'));
    this.formComponent = this.querySelector('product-form-component');
    this.variantInput = this.querySelector('[ref="variantId"]');
    this.addToCartComponent = this.querySelector('add-to-cart-component');
    this.addToCartButton = this.querySelector('[ref="addToCartButton"]');
    this.acceleratedCheckout = this.querySelector('[data-accelerated-checkout]');
    this.acceleratedVariantInput = this.querySelector('[data-accelerated-variant-input]');
    this.productDetails = this.closest('.product-details');
    this.productTitle = this.productDetails?.querySelector('h1 strong, h1');
    this.productPrice = this.productDetails?.querySelector('product-price');
    this.priceContainer = this.productPrice?.querySelector('[ref="priceContainer"]');
    this.valueCallout = this.productDetails?.querySelector('[data-dynamic-price-callout-text]');
    this.productMedia = this.closest('.product-information')?.querySelector('[data-testid="product-information-media"]');
    this.originalMediaGallery = this.productMedia?.querySelector('media-gallery');
    this.deliveryEstimate = this.querySelector('[data-delivery-estimate]');
    this.deliveryDateRange = this.querySelector('[data-delivery-date-range]');
    this.priceShippingNote = this.querySelector('[data-price-shipping-note]');
    this.stickyCart = this.querySelector('[data-sticky-cart]');
    this.stickyCartTitle = this.querySelector('[data-sticky-cart-title]');
    this.stickyCartPrice = this.querySelector('[data-sticky-cart-price]');
    this.stickyCartButton = this.querySelector('[data-sticky-cart-button]');

    if (this.productPrice && this.priceShippingNote) {
      this.productPrice.insertAdjacentElement('afterend', this.priceShippingNote);
      this.priceShippingNote.removeAttribute('hidden');
    }

    if (this.stickyCart) document.body.append(this.stickyCart);

    this.productTitle?.setAttribute('aria-live', 'polite');
    this.priceContainer?.setAttribute('aria-live', 'polite');
    this.deliveryDateRange?.setAttribute('aria-live', 'polite');
    this.updateDeliveryEstimate();
    this.setupStickyAddToCart();

    this.radios.forEach((radio) => {
      radio.addEventListener('change', () => this.selectOffer(radio));
    });

    const selectedRadio = this.radios.find((radio) => radio.checked && !radio.disabled);
    const fallbackRadio = this.radios.find((radio) => !radio.disabled);
    this.selectOffer(selectedRadio || fallbackRadio);
  }

  selectOffer(radio) {
    if (!radio) return;
    if (this.pendingStickyRequest && radio.dataset.variantId !== this.pendingStickyRequest.variantId) return;

    this.radios.forEach((candidate) => {
      const option = candidate.closest('[data-bundle-option]');
      const contents = option?.querySelector('[data-bundle-contents]');
      const isSelected = candidate === radio;
      candidate.checked = isSelected;
      option?.classList.toggle('is-selected', isSelected);
      if (contents) {
        option?.setAttribute('aria-expanded', String(isSelected));
        contents.setAttribute('aria-hidden', String(!isSelected));
      }
    });

    if (this.variantInput) this.variantInput.value = radio.dataset.variantId || '';
    if (this.formComponent && radio.dataset.productId) {
      this.formComponent.dataset.productId = radio.dataset.productId;
    }
    if (this.addToCartComponent && radio.dataset.productImage) {
      this.addToCartComponent.dataset.productVariantMedia = radio.dataset.productImage;
    }

    if (this.addToCartButton) {
      const unavailable = radio.dataset.available !== 'true';
      this.addToCartButton.toggleAttribute('disabled', unavailable);
      this.stickyCartButton?.toggleAttribute('disabled', unavailable);
    }

    const acceleratedVariantId = radio.dataset.variantId || '';
    const acceleratedButton = this.acceleratedCheckout?.querySelector('shopify-accelerated-checkout');
    if (acceleratedButton && acceleratedVariantId) {
      acceleratedButton.setAttribute('variant-params', JSON.stringify([{
        id: Number(acceleratedVariantId),
        requiresShipping: radio.dataset.requiresShipping !== 'false',
      }]));
    }
    if (this.acceleratedVariantInput) {
      this.acceleratedVariantInput.value = acceleratedVariantId;
      this.acceleratedVariantInput.setAttribute('value', acceleratedVariantId);
    }
    if (this.acceleratedCheckout) {
      this.acceleratedCheckout.dataset.acceleratedVariantId = acceleratedVariantId;
      this.acceleratedCheckout.toggleAttribute('hidden', radio.dataset.available !== 'true');
    }

    this.updateProductSummary(radio);
    this.updateShippingMessage(radio);
    this.updateStickyAddToCart(radio);
    this.updateProductGallery(radio);
  }

  setupStickyAddToCart() {
    if (!this.stickyCart || !this.stickyCartButton || !this.addToCartButton) return;

    this.stickyCartDefaultLabel = this.stickyCartButton.textContent.trim();

    this.stickyCartButton.addEventListener('click', () => {
      if (this.addToCartButton.disabled || this.pendingStickyRequest) return;

      const selectedRadio = this.radios.find((radio) => radio.checked);
      const variantId = this.variantInput?.value || '';
      if (!selectedRadio || !variantId) return;

      this.pendingStickyRequest = {
        variantId,
        productId: this.formComponent?.dataset.productId || '',
      };
      this.lockOfferSelection();
      this.setStickyCartButtonState('loading', 'Wird hinzugefügt…');
      this.stickyCartSlowRequestTimeout = setTimeout(() => {
        if (!this.pendingStickyRequest) return;
        this.setStickyCartButtonState('loading', 'Wird noch verarbeitet…');
      }, 10000);

      this.addToCartButton.dataset.dwkCtaLocationOverride = 'sticky';
      this.addToCartButton.click();
      delete this.addToCartButton.dataset.dwkCtaLocationOverride;
      this.addToCartButton.disabled = true;
    });

    this.handleStickyCartUpdate = (event) => {
      const pendingRequest = this.pendingStickyRequest;
      if (!pendingRequest || event.detail?.data?.source !== 'product-form-component') return;

      const isMatchingSuccess = !event.detail.data.didError
        && String(event.detail.sourceId) === String(pendingRequest.variantId);
      const isMatchingError = event.detail.data.didError
        && String(event.detail.sourceId) === String(pendingRequest.variantId);

      if (!isMatchingSuccess && !isMatchingError) return;

      this.finishStickyRequest();
      this.showStickyCartFeedback(
        isMatchingSuccess ? 'added' : 'error',
        isMatchingSuccess ? '✓ Hinzugefügt' : 'Nicht hinzugefügt'
      );
    };

    this.handleStickyCartError = (event) => {
      const pendingRequest = this.pendingStickyRequest;
      if (!pendingRequest || String(event.detail?.sourceId || '') !== String(pendingRequest.variantId)) return;

      this.finishStickyRequest();
      this.showStickyCartFeedback('error', 'Nicht hinzugefügt');
    };

    document.addEventListener('cart:update', this.handleStickyCartUpdate);
    window.addEventListener('cart:error', this.handleStickyCartError);

    this.stickyCartObserver = new IntersectionObserver(([entry]) => {
      const hasScrolledPastMainButton = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
      this.stickyCart.toggleAttribute('hidden', !hasScrolledPastMainButton);
    }, { threshold: 0 });

    this.stickyCartObserver.observe(this.addToCartButton);
  }

  lockOfferSelection() {
    if (this.radioDisabledStates) return;

    this.radioDisabledStates = new Map(this.radios.map((radio) => [radio, radio.disabled]));
    this.radios.forEach((radio) => {
      radio.disabled = true;
    });
    if (this.acceleratedCheckout) this.acceleratedCheckout.inert = true;
    this.setAttribute('aria-busy', 'true');
  }

  unlockOfferSelection() {
    if (!this.radioDisabledStates) return;

    this.radioDisabledStates.forEach((wasDisabled, radio) => {
      radio.disabled = wasDisabled;
    });
    this.radioDisabledStates = null;
    if (this.acceleratedCheckout) this.acceleratedCheckout.inert = false;
    const selectedRadio = this.radios.find((radio) => radio.checked);
    if (this.addToCartButton) this.addToCartButton.disabled = selectedRadio?.dataset.available !== 'true';
    this.removeAttribute('aria-busy');
  }

  finishStickyRequest() {
    clearTimeout(this.stickyCartSlowRequestTimeout);
    this.pendingStickyRequest = null;
    this.unlockOfferSelection();
  }

  setStickyCartButtonState(state, label) {
    if (!this.stickyCartButton) return;

    this.stickyCartButton.classList.remove('is-loading', 'is-added', 'is-error');
    if (state) this.stickyCartButton.classList.add(`is-${state}`);
    this.stickyCartButton.textContent = label;
    this.stickyCartButton.disabled = Boolean(state);
  }

  showStickyCartFeedback(state, label) {
    clearTimeout(this.stickyCartFeedbackTimeout);
    this.setStickyCartButtonState(state, label);
    this.stickyCartFeedbackTimeout = setTimeout(() => this.resetStickyCartButton(), 2000);
  }

  resetStickyCartButton() {
    if (!this.stickyCartButton) return;

    clearTimeout(this.stickyCartFeedbackTimeout);
    this.stickyCartButton.classList.remove('is-loading', 'is-added', 'is-error');
    this.stickyCartButton.textContent = this.stickyCartDefaultLabel || 'In den Warenkorb';
    const selectedRadio = this.radios?.find((radio) => radio.checked);
    this.stickyCartButton.disabled = selectedRadio?.dataset.available !== 'true';
  }

  updateStickyAddToCart(radio) {
    if (this.stickyCartTitle) {
      this.stickyCartTitle.textContent = radio.dataset.offerLabel || radio.dataset.productTitle || '';
    }

    if (this.stickyCartPrice && radio.dataset.price) {
      this.stickyCartPrice.textContent = radio.dataset.price;
    }

    if (!this.pendingStickyRequest) this.resetStickyCartButton();
  }

  updateShippingMessage(radio) {
    if (!this.deliveryEstimate) return;

    const priceCents = Number(radio.dataset.priceCents || 0);
    const thresholdCents = Number(this.deliveryEstimate.dataset.freeShippingThresholdCents || 0);
    const shippingConfigVerified = this.deliveryEstimate.dataset.shippingConfigVerified === 'true';
    const qualifiesForFreeShipping = shippingConfigVerified && thresholdCents > 0 && priceCents >= thresholdCents;

    if (this.priceShippingNote) {
      this.priceShippingNote.textContent = shippingConfigVerified
        ? qualifiesForFreeShipping
          ? 'inkl. MwSt. · versandkostenfrei'
          : 'inkl. MwSt. · zzgl. Versand'
        : 'inkl. MwSt. · Versand wird im Checkout berechnet';
    }
  }

  updateProductSummary(radio) {
    if (this.productTitle && radio.dataset.productTitle) {
      this.productTitle.textContent = radio.dataset.productTitle;
    }

    if (this.productPrice && radio.dataset.productId) {
      this.productPrice.dataset.productId = radio.dataset.productId;
    }

    if (this.priceContainer && radio.dataset.price) {
      const price = document.createElement('span');
      price.className = 'price';
      price.textContent = radio.dataset.price;

      this.priceContainer.replaceChildren();

      if (radio.dataset.compareAtPrice) {
        const compareAtPrice = document.createElement('s');
        compareAtPrice.className = 'compare-at-price';
        compareAtPrice.textContent = radio.dataset.compareAtPrice;
        this.priceContainer.append(compareAtPrice);
      }

      this.priceContainer.append(price);
    }

    if (this.valueCallout && radio.dataset.valueCallout) {
      this.valueCallout.textContent = radio.dataset.valueCallout;
    }
  }

  updateProductGallery(radio) {
    if (!this.productMedia) return;

    const currentGallery = this.productMedia.querySelector('[data-active-offer-gallery]');
    if (currentGallery?.dataset.activeOfferVariantId === radio.dataset.variantId) {
      this.originalMediaGallery?.setAttribute('hidden', '');
      this.setupProductGallery(currentGallery);
      return;
    }

    if (radio.dataset.useOriginalGallery === 'true') {
      currentGallery?.remove();
      this.originalMediaGallery?.removeAttribute('hidden');
      return;
    }

    const template = this.querySelector(`[data-bundle-gallery-template="${radio.dataset.variantId}"]`);
    if (!(template instanceof HTMLTemplateElement)) {
      this.originalMediaGallery?.removeAttribute('hidden');
      return;
    }

    this.originalMediaGallery?.setAttribute('hidden', '');
    const gallery = template.content.firstElementChild?.cloneNode(true);
    if (!(gallery instanceof HTMLElement)) return;

    currentGallery?.remove();
    this.prioritizeGalleryImages(gallery);
    this.productMedia.append(gallery);
    this.setupProductGallery(gallery);
  }

  prioritizeGalleryImages(gallery) {
    const images = Array.from(gallery.querySelectorAll('[data-offer-gallery-slide] img'));
    images.forEach((image, index) => {
      image.loading = index === 0 ? 'eager' : 'lazy';
      image.fetchPriority = index === 0 ? 'high' : 'low';
      image.decoding = 'async';
    });
  }

  setupProductGallery(gallery) {
    if (!(gallery instanceof HTMLElement) || gallery.dataset.galleryReady === 'true') return;
    gallery.dataset.galleryReady = 'true';
    this.prioritizeGalleryImages(gallery);

    const scroller = gallery.querySelector('[data-offer-gallery-scroller]');
    const slides = Array.from(gallery.querySelectorAll('[data-offer-gallery-slide]'));
    const thumbnails = Array.from(gallery.querySelectorAll('[data-offer-gallery-thumbnail]'));
    const dialog = gallery.querySelector('[data-offer-gallery-dialog]');
    const dialogImage = dialog?.querySelector('[data-offer-gallery-dialog-image]');
    const dialogImageWrap = dialog?.querySelector('[data-offer-gallery-dialog-image-wrap]');
    const dialogCounter = dialog?.querySelector('[data-offer-gallery-dialog-counter]');
    const dialogClose = dialog?.querySelector('[data-offer-gallery-dialog-close]');
    const dialogPrevious = dialog?.querySelector('[data-offer-gallery-dialog-previous]');
    const dialogNext = dialog?.querySelector('[data-offer-gallery-dialog-next]');
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let activeIndex = 0;
    let dialogIndex = 0;
    let dialogTrigger = null;
    let pointerStartX = 0;
    let suppressSlideClickUntil = 0;

    const selectThumbnail = (index) => {
      activeIndex = index;
      thumbnails.forEach((thumbnail, thumbnailIndex) => {
        const isSelected = thumbnailIndex === index;
        thumbnail.classList.toggle('is-selected', isSelected);
        thumbnail.setAttribute('aria-pressed', String(isSelected));
      });
    };

    const showDialogImage = (index) => {
      if (!(dialogImage instanceof HTMLImageElement) || slides.length === 0) return;
      dialogIndex = (index + slides.length) % slides.length;
      const sourceImage = slides[dialogIndex]?.querySelector('img');
      if (!(sourceImage instanceof HTMLImageElement)) return;

      dialogImage.src = sourceImage.currentSrc || sourceImage.src;
      dialogImage.srcset = sourceImage.srcset;
      dialogImage.sizes = '100vw';
      dialogImage.alt = sourceImage.alt;
      dialogImageWrap?.classList.remove('is-zoomed');
      if (dialogCounter) dialogCounter.textContent = `Bild ${dialogIndex + 1} von ${slides.length}`;
      dialogPrevious?.toggleAttribute('hidden', slides.length < 2);
      dialogNext?.toggleAttribute('hidden', slides.length < 2);
    };

    const openDialog = (index, trigger) => {
      if (!(dialog instanceof HTMLDialogElement)) return;
      dialogTrigger = trigger;
      showDialogImage(index);
      if (!dialog.open) dialog.showModal();
      dialogClose?.focus();
    };

    thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener('click', () => {
        const index = Number(thumbnail.dataset.offerGalleryThumbnail || 0);
        const slide = slides[index];
        if (!scroller || !slide) return;
        scroller.scrollTo({ left: slide.offsetLeft, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
        selectThumbnail(index);
      });
    });

    scroller?.addEventListener('pointerdown', (event) => {
      pointerStartX = event.clientX;
    }, { passive: true });

    scroller?.addEventListener('pointerup', (event) => {
      if (Math.abs(event.clientX - pointerStartX) > 8) suppressSlideClickUntil = Date.now() + 300;
    }, { passive: true });

    slides.forEach((slide, index) => {
      slide.addEventListener('click', () => {
        if (Date.now() < suppressSlideClickUntil) return;
        openDialog(index, slide);
      });
      slide.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        openDialog(index, slide);
      });
    });

    dialogClose?.addEventListener('click', () => dialog?.close());
    dialogPrevious?.addEventListener('click', () => showDialogImage(dialogIndex - 1));
    dialogNext?.addEventListener('click', () => showDialogImage(dialogIndex + 1));
    dialogImageWrap?.addEventListener('click', () => {
      dialogImageWrap.classList.toggle('is-zoomed');
    });
    dialog?.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') showDialogImage(dialogIndex - 1);
      if (event.key === 'ArrowRight') showDialogImage(dialogIndex + 1);
    });
    dialog?.addEventListener('close', () => {
      dialogImageWrap?.classList.remove('is-zoomed');
      dialogTrigger?.focus();
    });

    let scrollFrame;
    scroller?.addEventListener('scroll', () => {
      cancelAnimationFrame(scrollFrame);
      scrollFrame = requestAnimationFrame(() => {
        const index = Math.round(scroller.scrollLeft / Math.max(scroller.clientWidth, 1));
        selectThumbnail(Math.min(Math.max(index, 0), slides.length - 1));
      });
    }, { passive: true });

    selectThumbnail(activeIndex);
  }

  updateDeliveryEstimate() {
    if (!this.deliveryEstimate || !this.deliveryDateRange) return;

    const berlinParts = Object.fromEntries(
      new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Berlin',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        hourCycle: 'h23',
      })
        .formatToParts(new Date())
        .filter((part) => part.type !== 'literal')
        .map((part) => [part.type, part.value])
    );

    const today = new Date(Date.UTC(
      Number(berlinParts.year),
      Number(berlinParts.month) - 1,
      Number(berlinParts.day)
    ));
    const weekday = today.getUTCDay();
    const isBusinessDay = weekday >= 1 && weekday <= 5;
    const cutoffHour = Number(this.deliveryEstimate.dataset.cutoffHour || 13);
    const beforeCutoff = Number(berlinParts.hour) < cutoffHour;
    const earliestBusinessDays = isBusinessDay && beforeCutoff ? 1 : 2;
    const latestBusinessDays = 3;

    const addBusinessDays = (startDate, businessDays) => {
      const result = new Date(startDate);
      let addedDays = 0;

      while (addedDays < businessDays) {
        result.setUTCDate(result.getUTCDate() + 1);
        const resultWeekday = result.getUTCDay();
        if (resultWeekday !== 0 && resultWeekday !== 6) addedDays += 1;
      }

      return result;
    };

    const formatDate = new Intl.DateTimeFormat('de-DE', {
      timeZone: 'UTC',
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
    });

    const earliestDate = addBusinessDays(today, earliestBusinessDays);
    const latestDate = addBusinessDays(today, latestBusinessDays);
    this.deliveryDateRange.textContent = `${formatDate.format(earliestDate)} – ${formatDate.format(latestDate)}`;
  }
}

if (!customElements.get('pdp-bundle-selector')) {
  customElements.define('pdp-bundle-selector', PdpBundleSelector);
}
